/**
 * mot-r Design System Tokens
 * Adapted from react-prototype/theme.js
 */

export const theme = {
  colors: {
    // Brand
    brandPrimary: '#1e3a5f',
    brandNavy: '#1e3a5f',
    brandAccent: '#305CDE',
    brandAccentLight: '#F5FCFF',
    brandAccentMid: '#B7E9F7',

    // Semantic - Status
    success: '#10b981',
    successDark: '#059669',
    successLight: '#f0fdf4',
    warning: '#f59e0b',
    warningLight: '#fffbeb',
    error: '#dc2626',
    errorLight: '#fef2f2',
    info: '#3b82f6',
    infoLight: '#eff6ff',

    // Text
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    textLight: '#cbd5e1',
    textOnDark: '#ffffff',

    // Borders
    border: '#e2e8f0',
    borderLight: '#f1f5f9',

    // Dialog
    dialogBackground: '#1e3a5f',
    dialogFontColor: '#ffffff',

    // Backgrounds
    backgroundPage: '#f5f7fa',
    backgroundCard: '#ffffff',
    backgroundMuted: '#f8fafc',
    backgroundTableHeader: '#f8fafc',
    backgroundHover: '#fafbfc',
    bgPrimary: '#ffffff',
    bgSecondary: '#f8fafc',

    // Overlays
    overlayBackdrop: 'rgba(0, 0, 0, 0.5)',
    overlayBackdropLight: 'rgba(0, 0, 0, 0.3)',
    overlayWhite: 'rgba(255, 255, 255, 0.9)',
    overlayWhiteMuted: 'rgba(255, 255, 255, 0.15)',
    overlayWhiteSoft: 'rgba(255, 255, 255, 0.7)',
    // Additional overlays for dark backgrounds (stepper, headers)
    overlayWhiteMedium: 'rgba(255, 255, 255, 0.4)',
    overlayWhiteLight: 'rgba(255, 255, 255, 0.25)',
    overlayWhiteSubtle: 'rgba(255, 255, 255, 0.3)',
    overlayDark: 'rgba(0, 0, 0, 0.6)',
  },

  typography: {
    fontFamily: "'Open Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSizeXs: '11px',
    fontSizeSm: '12px',
    fontSizeBase: '14px',
    fontSizeMd: '15px',
    fontSizeLg: '17px',
    fontSizeXl: '18px',
    fontSize2xl: '20px',
    fontSizeKpi: '30px',
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    fontWeightBold: 700,
    lineHeightTight: 1.25,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
  },

  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '10px',
    xxl: '12px',
    full: '50%',
  },

  borders: {
    default: '1px solid #e2e8f0',
    subtle: '1px solid #f1f5f9',
    active: '2px solid #305CDE',
    focusRing: '2px solid #305CDE',
    focusRingOffset: '2px',
    indicatorWidth: '4px',
  },

  shadows: {
    xs: '0 1px 2px rgba(0,0,0,0.04)',
    card: '0 1px 3px rgba(0,0,0,0.06)',
    dropdown: '0 4px 12px rgba(0,0,0,0.15)',
    modal: '0 25px 50px -12px rgba(0,0,0,0.25)',
  },

  // Z-index scale — centralized to prevent stacking conflicts
  zIndex: {
    base: 1,
    dropdown: 100,
    sticky: 200,
    sidebar: 200,
    sidebarBackdrop: 199,
    flyout: 300,
    fixed: 400,
    modalBackdrop: 1000,
    modal: 1100,
    toast: 9999,
    spinner: 9999,
  },

  transitions: {
    default: '0.15s',
    fast: '0.1s',
    staggerDelay: 0.03,
  },

  // Severity indicator colors
  severity: {
    high: { border: '#dc2626', bg: '#fef2f2' },
    medium: { border: '#f59e0b', bg: '#fffbeb' },
    low: { border: '#3b82f6', bg: '#eff6ff' },
    none: { border: 'transparent', bg: '#ffffff' },
  },

  // Outcome sentiment colors
  outcomes: {
    positive: '#10b981',
    negative: '#f59e0b',
    terminated: '#6b7280',
  },

  // Spinner configuration
  // Change 'defaultVariant' to update spinners app-wide
  // Options: 'modern' | 'classic' | 'dots' | 'progress'
  spinner: {
    defaultVariant: 'modern' as const,
    defaultSize: 'md' as const,
    defaultColor: '#305CDE', // brandAccent
  },

  // Sidebar styling
  // Customize the left navigation menu appearance
  sidebar: {
    background: '#1e3a5f', // Default matches brandPrimary
    text: '#ffffff',
    textMuted: '#b8c5d3', // Light gray for inactive items (hex for better color picker support)
    activeBackground: '#2d4a6f', // Slightly lighter than background
    hoverBackground: '#294563', // Between background and active
    border: '#3d5a7f', // Visible border color
    activeText: '#ffffff',
    iconColor: '#b8c5d3', // Match textMuted
    iconActiveColor: '#ffffff',
    sidebarMode: 'dark' as 'light' | 'dark', // 'dark' = dark sidebar with light logo, 'light' = light sidebar with dark logo
    logoLightUrl: '' as string, // Custom light logo URL (for dark backgrounds)
    logoDarkUrl: '' as string, // Custom dark logo URL (for light backgrounds)
    // Font sizes for sidebar menu items
    fontSizeParent: '17px', // Top-level menu items (depth 0)
    fontSizeChild: '14px', // Sub-menu items (depth 1+)
    fontSizeHeader: '18px', // Company name / logo text
  },
} as const;

// Sidebar mode presets — used by StyleConfigPanel to batch-update sidebar colors
export const SIDEBAR_DARK_PRESET: Record<string, string> = {
  background: '#1e3a5f',
  text: '#ffffff',
  textMuted: '#b8c5d3',
  activeBackground: '#2d4a6f',
  activeText: '#ffffff',
  hoverBackground: '#294563',
  border: '#3d5a7f',
  iconColor: '#b8c5d3',
  iconActiveColor: '#ffffff',
};

export const SIDEBAR_LIGHT_PRESET: Record<string, string> = {
  background: '#ffffff',
  text: '#1e293b',
  textMuted: '#64748b',
  activeBackground: '#eff6ff',
  activeText: '#1e3a5f',
  hoverBackground: '#f1f5f9',
  border: '#e2e8f0',
  iconColor: '#64748b',
  iconActiveColor: '#1e3a5f',
};
